import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getExtAdminProfile,
  updateExtAdminProfile,
  changeExtAdminPassword,
} from "../../../redux/actions/profile";
import Flash from "../../../utility/Flash";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

function SectionCard({ title, icon, children }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <i className={icon} style={{ color: PRIMARY, marginRight: 8, fontSize: 18 }} />
        <span style={styles.cardTitle}>{title}</span>
      </div>
      <div style={styles.cardBody}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={styles.field}>
      <label style={styles.fieldLabel}>{label}</label>
      {children}
    </div>
  );
}

export default function Settings() {
  const dispatch = useDispatch();
  const profile = useSelector((s) => s.profile.extAdminProfile);
  const dashboard = useSelector((s) => s.profile.extAdminDashboard);

  const [profileForm, setProfileForm] = useState({ fullname: "", phone: "" });
  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    dispatch(getExtAdminProfile());
  }, [dispatch]);

  useEffect(() => {
    if (profile) {
      setProfileForm({ fullname: profile.fullname || "", phone: profile.phone || "" });
    }
  }, [profile]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    const result = await dispatch(updateExtAdminProfile(profileForm));
    setProfileSaving(false);
    if (result?.success) {
      Flash("success", "Profile updated successfully", "", 3000);
    } else {
      Flash("error", result?.message || "Update failed", "", 3000);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      return Flash("error", "New passwords do not match", "", 3000);
    }
    if (pwForm.newPassword.length < 8) {
      return Flash("error", "Password must be at least 8 characters", "", 3000);
    }
    setPwSaving(true);
    const result = await dispatch(changeExtAdminPassword({
      currentPassword: pwForm.currentPassword,
      newPassword: pwForm.newPassword,
    }));
    setPwSaving(false);
    if (result?.success) {
      Flash("success", "Password changed successfully", "", 3000);
      setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } else {
      Flash("error", result?.message || "Failed to change password", "", 3000);
    }
  };

  const refCode = dashboard?.adminRefCode || profile?.adminRefCode || "—";

  return (
    <div style={styles.root}>
      <h2 style={styles.heading}>Settings</h2>

      {/* Account Info Banner */}
      <div style={styles.infoBanner}>
        <div style={styles.avatarCircle}>
          <i className="bi bi-person-fill" style={{ fontSize: 28, color: "#fff" }} />
        </div>
        <div>
          <div style={styles.infoName}>{profile?.fullname || profile?.email || "Sales Admin"}</div>
          <div style={styles.infoEmail}>{profile?.email}</div>
          <div style={styles.infoBadge}>
            <i className="bi bi-shield-check" style={{ color: PRIMARY, marginRight: 4 }} />
            EX-DSA-EXT-ADMIN
            {refCode !== "—" && (
              <span style={{ marginLeft: 10, color: PRIMARY, fontWeight: 700, letterSpacing: 1 }}>
                · Ref: {refCode}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Profile Update */}
      <SectionCard title="Profile Information" icon="bi bi-person-lines-fill">
        <form onSubmit={handleProfileSave}>
          <div style={styles.formGrid}>
            <Field label="Full Name">
              <input
                style={styles.input}
                placeholder="Your full name"
                value={profileForm.fullname}
                onChange={(e) => setProfileForm({ ...profileForm, fullname: e.target.value })}
              />
            </Field>
            <Field label="Phone Number">
              <input
                style={styles.input}
                placeholder="e.g. 08012345678"
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Email Address">
            <input
              style={{ ...styles.input, background: "#f5f5f5", color: "#999", cursor: "not-allowed" }}
              value={profile?.email || ""}
              disabled
              title="Email cannot be changed"
            />
          </Field>
          <button type="submit" style={styles.btnPrimary} disabled={profileSaving}>
            {profileSaving ? "Saving…" : "Save Changes"}
          </button>
        </form>
      </SectionCard>

      {/* Change Password */}
      <SectionCard title="Change Password" icon="bi bi-lock-fill">
        <form onSubmit={handlePasswordChange}>
          <Field label="Current Password">
            <input
              style={styles.input}
              type="password"
              placeholder="Enter your current password"
              value={pwForm.currentPassword}
              onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })}
              autoComplete="current-password"
            />
          </Field>
          <div style={styles.formGrid}>
            <Field label="New Password">
              <input
                style={styles.input}
                type="password"
                placeholder="At least 8 characters"
                value={pwForm.newPassword}
                onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })}
                autoComplete="new-password"
              />
            </Field>
            <Field label="Confirm New Password">
              <input
                style={styles.input}
                type="password"
                placeholder="Repeat new password"
                value={pwForm.confirmPassword}
                onChange={(e) => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
                autoComplete="new-password"
              />
            </Field>
          </div>
          <button type="submit" style={styles.btnPrimary} disabled={pwSaving}>
            {pwSaving ? "Updating…" : "Change Password"}
          </button>
        </form>
      </SectionCard>
    </div>
  );
}

const styles = {
  root: { paddingBottom: 40, maxWidth: 720 },
  heading: { fontSize: 22, fontWeight: 700, color: BG, marginBottom: 24 },
  infoBanner: {
    background: BG, borderRadius: 12, padding: "20px 24px",
    display: "flex", alignItems: "center", gap: 18, marginBottom: 28
  },
  avatarCircle: {
    width: 60, height: 60, borderRadius: "50%",
    background: "rgba(167,204,72,0.25)", display: "flex",
    alignItems: "center", justifyContent: "center", flexShrink: 0
  },
  infoName: { fontSize: 18, fontWeight: 700, color: "#fff" },
  infoEmail: { fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 2 },
  infoBadge: { fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 6, display: "flex", alignItems: "center" },
  card: {
    background: "#fff", borderRadius: 10, padding: "22px 24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: 20
  },
  cardHeader: { display: "flex", alignItems: "center", marginBottom: 20, paddingBottom: 14, borderBottom: "1px solid #f0f0f0" },
  cardTitle: { fontSize: 15, fontWeight: 700, color: BG },
  cardBody: {},
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" },
  field: { marginBottom: 16 },
  fieldLabel: { display: "block", fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 },
  input: {
    width: "100%", border: "1.5px solid #e0e0e0", borderRadius: 7,
    padding: "10px 14px", fontSize: 14, outline: "none",
    boxSizing: "border-box", transition: "border-color .2s"
  },
  btnPrimary: {
    background: PRIMARY, color: "#fff", border: "none", borderRadius: 7,
    padding: "10px 24px", cursor: "pointer", fontSize: 14, fontWeight: 700,
    marginTop: 6
  },
};
